/**
 * Agent Component Generator
 *
 * Dedicated component generation + validation pipeline for the agent architecture.
 * Replaces the use of base-llm's matchComponentsFromAnalysis() in the agent flow.
 *
 * Key differences from the old flow:
 * - Validates externalTool SQL via external-tools.execute (not database.execute)
 * - Uses per-source schema and database rules for LLM query fixes
 * - Components without valid SQL are excluded (not sent to frontend broken)
 */

import { Component } from '../../types';
import { ensureQueryLimit, validateAndFixSqlQuery, convertQuestionsToActions, type DatabaseType } from '../utils';
import { promptLoader } from '../prompt-loader';
import { LLM } from '../../llm';
import { logger } from '../../utils/logger';
import { getCurrentDateTimeForPrompt } from '../../utils/datetime';
import { queryCache } from '../../utils/query-cache';
import { processComponentProps } from '../utils/component-props-processor';
import { extractPromptText } from '../prompt-extractor';
import KB from '../knowledge-base';
import type { Action } from '../../threads/action';
import type { ExecutedToolInfo } from '../services/tool-executor-service';
import {
	MAX_QUERY_VALIDATION_RETRIES,
	MAX_TOKENS_COMPONENT_MATCHING,
	MAX_COMPONENT_QUERY_LIMIT,
	DEFAULT_QUERY_LIMIT,
	KNOWLEDGE_BASE_TOP_K,
} from '../constants';

// ============================================
// Types
// ============================================

export interface AgentComponentGeneratorParams {
	analysisContent: string;
	components: Component[];
	userPrompt: string;
	executedTools: ExecutedToolInfo[];
	collections: any;
	apiKey?: string;
	/** Model for component generation + SQL fixes (e.g. the configured
	 *  sourceAgentModel) so this follows the deployment's provider choice.
	 *  Defaults to Anthropic haiku when unset. */
	model?: string;
	componentStreamCallback?: (component: Component) => void;
	userId?: string;
	/** Per-turn cancellation — aborts the component-generation + SQL-fix LLM calls if the user hit Stop. */
	signal?: AbortSignal;
}

/** Default model for component matching when the caller doesn't specify one. */
const DEFAULT_COMPONENT_MODEL = 'anthropic/claude-haiku-4-5-20251001';

export interface AgentComponentGeneratorResult {
	components: Component[];
	layoutTitle: string;
	layoutDescription: string;
	actions: Action[];
}

// ============================================
// Main Entry Point
// ============================================

/**
 * Generate and validate components for the agent architecture.
 *
 * Steps:
 * A. Build prompt context (available components, executed tools, KB, rules)
 * B. LLM call to generate components JSON
 * C. Build final Component objects from LLM output
 * D. Validate externalTool SQL queries (execute, retry with LLM fix, exclude on failure)
 * E. Return validated components + layout + actions
 */
export async function generateAgentComponents(
	params: AgentComponentGeneratorParams
): Promise<AgentComponentGeneratorResult> {
	const startTime = Date.now();
	const {
		analysisContent, components, userPrompt,
		executedTools, collections, apiKey, model,
		componentStreamCallback, userId, signal,
	} = params;

	logger.info(`[AgentComponentGen] Starting | ${executedTools.length} executed tools | ${components.length} available components`);

	try {
		// ============================================
		// STEP A: Build prompt context
		// ============================================

		const availableComponentsText = formatAvailableComponents(components);
		const executedToolsText = formatExecutedTools(executedTools);

		// Collect database rules from all source types used
		const sourceTypes = [...new Set(executedTools.map(t => t.sourceType).filter(Boolean))];
		let databaseRules = '';
		if (sourceTypes.length > 0) {
			const rulesArr = await Promise.all(
				sourceTypes.map(st => promptLoader.loadDatabaseRulesForType(st!))
			);
			databaseRules = rulesArr.join('\n\n');
		} else {
			databaseRules = await promptLoader.loadDatabaseRules();
		}

		// Knowledge base context — split global (static/cached) vs user+query (dynamic)
		let globalKnowledgeBase = 'No global knowledge base available.';
		let knowledgeBaseContext = 'No additional knowledge base context available.';
		if (collections) {
			const kbResult = await KB.getAllKnowledgeBase({
				prompt: userPrompt || analysisContent,
				collections,
				userId,
				topK: KNOWLEDGE_BASE_TOP_K,
			});
			globalKnowledgeBase = kbResult.globalContext || globalKnowledgeBase;
			const dynamicParts: string[] = [];
			if (kbResult.userContext) {
				dynamicParts.push('## User-Specific Knowledge Base\n' + kbResult.userContext);
			}
			if (kbResult.queryContext) {
				dynamicParts.push('## Relevant Knowledge Base (Query-Matched)\n' + kbResult.queryContext);
			}
			knowledgeBaseContext = dynamicParts.join('\n\n') || knowledgeBaseContext;
		}

		const prompts = await promptLoader.loadPrompts('match-text-components', {
			USER_PROMPT: userPrompt || '',
			ANALYSIS_CONTENT: analysisContent,
			AVAILABLE_COMPONENTS: availableComponentsText,
			SCHEMA_DOC: 'Use column names from executed query results in EXECUTED_TOOLS.',
			DATABASE_RULES: databaseRules,
			DEFERRED_TOOLS: 'No deferred external tools for this request.',
			EXECUTED_TOOLS: executedToolsText,
			GLOBAL_KNOWLEDGE_BASE: globalKnowledgeBase,
			KNOWLEDGE_BASE_CONTEXT: knowledgeBaseContext,
			CURRENT_DATETIME: getCurrentDateTimeForPrompt(),
		});

		logger.logLLMPrompt('agentComponentGen', 'system', extractPromptText(prompts.system));
		logger.logLLMPrompt('agentComponentGen', 'user', `Text Analysis:\n${analysisContent}\n\nExecuted Tools:\n${executedToolsText}`);

		// ============================================
		// STEP B: LLM call — generate components JSON
		// ============================================

		let fullResponseText = '';
		let answerComponentStreamed = false;

		const partialCallback = componentStreamCallback ? (chunk: string) => {
			fullResponseText += chunk;

			// Stream answerComponent early if detected
			if (!answerComponentStreamed && componentStreamCallback) {
				const streamed = tryStreamAnswerComponent(
					fullResponseText, components, executedTools,
					collections, apiKey, componentStreamCallback
				);
				if (streamed) answerComponentStreamed = true;
			}
		} : undefined;

		const result = await LLM.stream<any>(
			{ sys: prompts.system, user: prompts.user },
			{
				model: model || DEFAULT_COMPONENT_MODEL,
				maxTokens: MAX_TOKENS_COMPONENT_MATCHING,
				temperature: 0,
				apiKey,
				partial: partialCallback,
				signal,
			},
			true // Parse as JSON
		) as any;

		let matchedComponents = result.matchedComponents || [];
		const layoutTitle = result.layoutTitle || 'Dashboard';
		const layoutDescription = result.layoutDescription || 'Multi-component dashboard';

		const vizComps = matchedComponents.filter((mc: any) => mc.componentName !== 'DynamicMarkdownBlock');
		logger.info(`[AgentComponentGen] ${vizComps.length} visualizations, ${matchedComponents.length - vizComps.length} markdown blocks`);

		// Ensure answerComponent is in matchedComponents (avoid duplicates)
		if (result.hasAnswerComponent && result.answerComponent?.componentId) {
			const answer = result.answerComponent;
			const answerSql = answer.props?.externalTool?.parameters?.sql || '';
			const answerTitle = answer.props?.title || '';
			const answerType = answer.componentName || '';

			const isDuplicate = matchedComponents.some((mc: any) => {
				const mcSql = mc.props?.externalTool?.parameters?.sql || '';
				const mcTitle = mc.props?.title || '';
				const mcType = mc.componentName || '';
				return mcType === answerType && (mcTitle === answerTitle || mcSql === answerSql);
			});

			if (!isDuplicate) {
				matchedComponents.unshift(answer);
			}
		}

		logger.info(`[AgentComponentGen] LLM returned ${matchedComponents.length} components`);
		matchedComponents.forEach((comp: any, idx: number) => {
			logger.info(`[AgentComponentGen]   ${idx + 1}. ${comp.componentType} (${comp.componentName})`);
		});

		logger.file('\n=============================\nFull LLM response:', JSON.stringify(result, null, 2));

		const rawActions = result.actions || [];
		const actions = convertQuestionsToActions(rawActions);

		// ============================================
		// STEP C: Build final Component objects
		// ============================================

		const finalComponents: Component[] = matchedComponents.map((mc: any) => {
			const originalComponent = components.find(c => c.name === mc.componentName);
			if (!originalComponent) {
				logger.warn(`[AgentComponentGen] Component "${mc.componentName}" not found in available components`);
				return null;
			}

			const cleanedProps = processComponentProps(
				mc.props, executedTools,
				{ providerName: 'AgentComponentGen', defaultLimit: DEFAULT_QUERY_LIMIT, maxLimit: MAX_COMPONENT_QUERY_LIMIT }
			);

			return {
				...originalComponent,
				props: { ...originalComponent.props, ...cleanedProps },
			};
		}).filter(Boolean) as Component[];

		// ============================================
		// STEP D: Validate externalTool SQL queries
		// ============================================

		const validatedComponents = await validateExternalToolQueries(
			finalComponents, collections, executedTools, apiKey, model, signal
		);

		const elapsed = Date.now() - startTime;
		logger.info(`[AgentComponentGen] Complete | ${validatedComponents.length}/${finalComponents.length} validated | ${elapsed}ms`);

		return {
			components: validatedComponents,
			layoutTitle,
			layoutDescription,
			actions,
		};

	} catch (error) {
		const errorMsg = error instanceof Error ? error.message : String(error);
		logger.error(`[AgentComponentGen] Error: ${errorMsg}`);
		return {
			components: [],
			layoutTitle: 'Dashboard',
			layoutDescription: '',
			actions: [],
		};
	}
}

// ============================================
// Prompt Formatting Helpers
// ============================================

function formatAvailableComponents(components: Component[]): string {
	if (!components || components.length === 0) return 'No components available';

	return components.map((comp, idx) => {
		const keywords = comp.keywords ? comp.keywords.join(', ') : '';
		const propsPreview = comp.props ? JSON.stringify(comp.props, null, 2) : 'No props';
		return `${idx + 1}. ID: ${comp.id}
   Name: ${comp.name}
   Type: ${comp.type}
   Description: ${comp.description || 'No description'}
   Keywords: ${keywords}
   Props Structure: ${propsPreview}`;
	}).join('\n\n');
}

function formatExecutedTools(executedTools: ExecutedToolInfo[]): string {
	if (!executedTools || executedTools.length === 0) {
		return 'No external tools were executed for data fetching.';
	}

	return 'The following external tools were executed to fetch data.\n' +
		executedTools.map((tool, idx) => {
			let outputSchemaText = 'Not available';
			let fieldNamesList = '';

			const recordCount = tool.result?._totalRecords ?? 'unknown';

			let metadataText = '';
			if (tool.result?._metadata && Object.keys(tool.result._metadata).length > 0) {
				const metadataEntries = Object.entries(tool.result._metadata)
					.map(([key, value]) => `${key}: ${value}`)
					.join(', ');
				metadataText = `\n   📋 METADATA: ${metadataEntries}`;
			}

			if (tool.outputSchema) {
				const fields = tool.outputSchema.fields || [];
				const numericFields = fields.filter((f: any) => f.type === 'number').map((f: any) => f.name);
				const stringFields = fields.filter((f: any) => f.type === 'string').map((f: any) => f.name);

				fieldNamesList = `
   📊 NUMERIC FIELDS (use for yAxisKey, valueKey, aggregationField): ${numericFields.join(', ') || 'none'}
   📝 STRING FIELDS (use for xAxisKey, groupBy, nameKey): ${stringFields.join(', ') || 'none'}`;

				const fieldsText = fields.map((f: any) =>
					`     "${f.name}" (${f.type}): ${f.description}`
				).join('\n');
				outputSchemaText = `${tool.outputSchema.description}\n   Fields:\n${fieldsText}`;
			}

			// Show sample rows so the LLM picks correct xAxis/yAxis/valueKey fields and sees
			// type + range variation. Defensive cap: even if a tool slipped a large object
			// past upstream truncation, never inject more than ~8 KB of sample JSON into
			// the prompt.
			const MAX_SAMPLE_BLOCK_CHARS = 8000;
			let sampleDataText = '';
			const sampleData = tool.result?._sampleData;
			if (Array.isArray(sampleData) && sampleData.length > 0) {
				const sampleFields = Object.keys(sampleData[0]);
				sampleDataText = `\n   🔑 RESULT FIELDS: ${sampleFields.join(', ')}`;
				try {
					const stringified = JSON.stringify(sampleData, null, 2);
					const capped = stringified.length > MAX_SAMPLE_BLOCK_CHARS
						? `${stringified.substring(0, MAX_SAMPLE_BLOCK_CHARS)}\n... (truncated; ${stringified.length - MAX_SAMPLE_BLOCK_CHARS} more chars)`
						: stringified;
					sampleDataText += `\n   📄 SAMPLE ROWS (${sampleData.length}): ${capped}`;
				} catch { /* skip if serialization fails */ }
			}

			return `${idx + 1}. **${tool.name}**
   toolId: "${tool.id}"
   toolName: "${tool.name}"
   parameters: ${JSON.stringify(tool.params || {})}
   recordCount: ${recordCount} rows returned${metadataText}
   outputSchema: ${outputSchemaText}${fieldNamesList}${sampleDataText}`;
		}).join('\n\n');
}

// ============================================
// Answer Component Streaming
// ============================================

/**
 * Try to extract and stream answerComponent from partial LLM response.
 * Returns true if successfully streamed.
 */
function tryStreamAnswerComponent(
	text: string,
	components: Component[],
	executedTools: ExecutedToolInfo[],
	collections: any,
	apiKey: string | undefined,
	callback: (component: Component) => void
): boolean {
	// Check hasAnswerComponent flag
	const hasMatch = text.match(/"hasAnswerComponent"\s*:\s*(true|false)/);
	if (!hasMatch || hasMatch[1] !== 'true') return false;

	// Find answerComponent object
	const startMatch = text.match(/"answerComponent"\s*:\s*\{/);
	if (!startMatch) return false;

	const startPos = startMatch.index! + startMatch[0].length - 1;

	// Track braces to find complete object
	let depth = 0;
	let inString = false;
	let escapeNext = false;
	let endPos = -1;

	for (let i = startPos; i < text.length; i++) {
		const char = text[i];
		if (escapeNext) { escapeNext = false; continue; }
		if (char === '\\') { escapeNext = true; continue; }
		if (char === '"') { inString = !inString; continue; }
		if (!inString) {
			if (char === '{') depth++;
			else if (char === '}') {
				depth--;
				if (depth === 0) { endPos = i + 1; break; }
			}
		}
	}

	if (endPos <= startPos) return false;

	try {
		const answerData = JSON.parse(text.substring(startPos, endPos));
		if (!answerData?.componentId) return false;

		const original = components.find(c => c.id === answerData.componentId);
		if (!original) return false;

		const answerComponent: Component = {
			...original,
			props: { ...original.props, ...answerData.props },
		};

		// Validate externalTool query before streaming
		const answerProps = answerComponent.props as any;
		let sql = answerProps?.externalTool?.parameters?.sql;
		if (sql && collections?.['external-tools']?.['execute']) {
			const toolId = answerProps.externalTool.toolId;
			const toolName = answerProps.externalTool.toolName;

			// Apply the same query limit that final validation uses,
			// so the streamed answer and final component show identical data
			const executedTool = executedTools.find(t => t.id === toolId);
			const dbType = executedTool?.sourceType as DatabaseType | undefined;
			if (dbType) {
				sql = ensureQueryLimit(sql, DEFAULT_QUERY_LIMIT, MAX_COMPONENT_QUERY_LIMIT, dbType);
				answerProps.externalTool.parameters.sql = sql;
			}

			// Async validation — stream on success, skip on failure
			(async () => {
				try {
					const result = await collections['external-tools']['execute']({
						toolId, toolName, sql, data: {},
					});
					if (result?.success === false || result?.error) {
						logger.warn(`[AgentComponentGen] Answer component query failed: ${result?.error}`);
						return;
					}
					const cacheKey = toolId ? `${toolId}:${sql}` : sql;
					queryCache.set(cacheKey, result?.data ?? result);
					callback(answerComponent);
				} catch (err) {
					const msg = err instanceof Error ? err.message : String(err);
					logger.warn(`[AgentComponentGen] Answer component validation failed: ${msg}`);
				}
			})();
		} else {
			// No SQL to validate (markdown, non-SQL tools) — stream immediately
			callback(answerComponent);
		}

		return true;
	} catch {
		return false;
	}
}

// ============================================
// ExternalTool Query Validation
// ============================================

/**
 * Validate all component externalTool SQL queries.
 * Executes each query, retries with LLM fix on failure, excludes on max retries.
 */
async function validateExternalToolQueries(
	components: Component[],
	collections: any,
	executedTools: ExecutedToolInfo[],
	apiKey?: string,
	model?: string,
	signal?: AbortSignal,
): Promise<Component[]> {
	if (!collections?.['external-tools']?.['execute']) {
		logger.warn(`[AgentComponentGen] external-tools.execute not available, skipping validation`);
		return components;
	}

	const validated: Component[] = [];

	// Separate into components with and without externalTool SQL
	const withSql: Component[] = [];
	const withoutSql: Component[] = [];

	for (const comp of components) {
		const sql = (comp.props as any)?.externalTool?.parameters?.sql;
		if (sql) {
			withSql.push(comp);
		} else {
			withoutSql.push(comp);
		}
	}

	// Components without SQL pass through
	validated.push(...withoutSql);

	if (withSql.length === 0) return validated;

	// Deduplicate: group components by SQL so identical queries are validated only once
	const sqlGroups = new Map<string, Component[]>();
	for (const comp of withSql) {
		const sql = (comp.props as any).externalTool.parameters.sql;
		const normalized = sql.replace(/\s+/g, ' ').trim();
		if (!sqlGroups.has(normalized)) {
			sqlGroups.set(normalized, []);
		}
		sqlGroups.get(normalized)!.push(comp);
	}

	const uniqueQueries = Array.from(sqlGroups.entries());
	logger.info(`[AgentComponentGen] Validating ${uniqueQueries.length} unique queries (${withSql.length} components)...`);

	// Validate unique queries in parallel (use first component from each group)
	const results = await Promise.allSettled(
		uniqueQueries.map(([_, comps]) => validateSingleExternalToolQuery(comps[0], collections, executedTools, apiKey, model, signal))
	);

	for (let i = 0; i < results.length; i++) {
		const result = results[i];
		const [_, groupComps] = uniqueQueries[i];

		if (result.status === 'fulfilled' && result.value) {
			// First component gets the validated result, others reuse it
			validated.push(result.value);
			for (let j = 1; j < groupComps.length; j++) {
				// Copy the (possibly fixed) SQL to sibling components
				const fixedSql = (result.value.props as any)?.externalTool?.parameters?.sql;
				if (fixedSql) {
					const siblingProps = groupComps[j].props as any;
					validated.push({
						...groupComps[j],
						props: {
							...groupComps[j].props,
							externalTool: {
								...siblingProps.externalTool,
								parameters: { ...siblingProps.externalTool.parameters, sql: fixedSql },
							},
						},
					});
				} else {
					validated.push(groupComps[j]);
				}
			}
		} else {
			const reason = result.status === 'rejected' ? result.reason : 'validation failed';
			for (const comp of groupComps) {
				logger.warn(`[AgentComponentGen] Excluded ${comp.name}: ${reason}`);
			}
		}
	}

	logger.info(`[AgentComponentGen] Validation complete: ${validated.length}/${components.length} components passed`);
	return validated;
}

/**
 * Validate a single component's externalTool SQL query with retry logic.
 * Returns the (possibly fixed) component on success, null on failure.
 */
async function validateSingleExternalToolQuery(
	component: Component,
	collections: any,
	executedTools: ExecutedToolInfo[],
	apiKey?: string,
	model?: string,
	signal?: AbortSignal,
): Promise<Component | null> {
	const compProps = component.props as any;
	const toolId = compProps?.externalTool?.toolId;
	const toolName = compProps?.externalTool?.toolName;
	let currentSql = compProps?.externalTool?.parameters?.sql;

	if (!toolId || !currentSql) return component;

	// Determine database type from executed tool for correct SQL syntax (TOP vs LIMIT)
	const executedTool = executedTools.find(t => t.id === toolId);
	const dbType = executedTool?.sourceType as DatabaseType | undefined;

	// Only enforce query limit when source type is known — unknown sources
	// may have syntax that ensureQueryLimit doesn't understand
	if (dbType) {
		currentSql = ensureQueryLimit(currentSql, DEFAULT_QUERY_LIMIT, MAX_COMPONENT_QUERY_LIMIT, dbType);
	}

	let attempts = 0;

	while (attempts < MAX_QUERY_VALIDATION_RETRIES) {
		attempts++;

		try {
			logger.info(`[AgentComponentGen] Validating ${component.name} (attempt ${attempts}/${MAX_QUERY_VALIDATION_RETRIES})`);

			const result = await collections['external-tools']['execute']({
				toolId, toolName, sql: currentSql, data: {},
			});

			if (result?.success === false || result?.error) {
				const errorMsg = result?.error || 'Unknown error';
				throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
			}

			// Success — cache the raw tool data (not the { success, data, message } wrapper)
			// so that executeByQueryId returns the same structure cached vs non-cached
			const rawToolData = result?.data ?? result;
			const cacheKey = toolId ? `${toolId}:${currentSql}` : currentSql;
			queryCache.set(cacheKey, rawToolData);
			logger.info(`[AgentComponentGen] ✓ ${component.name} validated (attempt ${attempts})`);

			// Update component with (possibly fixed) SQL
			return {
				...component,
				props: {
					...component.props,
					externalTool: {
						...compProps.externalTool,
						parameters: {
							...compProps.externalTool.parameters,
							sql: currentSql,
						},
					},
				},
			};

		} catch (error) {
			const errorMsg = error instanceof Error ? error.message : String(error);
			logger.warn(`[AgentComponentGen] ✗ ${component.name} failed (attempt ${attempts}): ${errorMsg}`);

			if (attempts >= MAX_QUERY_VALIDATION_RETRIES) {
				logger.error(`[AgentComponentGen] Max retries reached for ${component.name}, excluding`);
				return null;
			}

			// Try to fix with LLM
			try {
				const fixedSql = await requestExternalToolQueryFix(
					currentSql, errorMsg, component, executedTools, toolId, apiKey, model, signal
				);

				if (fixedSql && fixedSql !== currentSql) {
					currentSql = dbType ? ensureQueryLimit(fixedSql, DEFAULT_QUERY_LIMIT, MAX_COMPONENT_QUERY_LIMIT, dbType) : fixedSql;
					logger.info(`[AgentComponentGen] LLM provided fix for ${component.name}, retrying...`);
				} else {
					logger.warn(`[AgentComponentGen] LLM returned same or empty query, stopping retries`);
					return null;
				}
			} catch (fixError) {
				const fixMsg = fixError instanceof Error ? fixError.message : String(fixError);
				logger.error(`[AgentComponentGen] Failed to get LLM fix: ${fixMsg}`);
				return null;
			}
		}
	}

	return null;
}

// ============================================
// LLM Query Fix
// ============================================

/**
 * Request LLM to fix a failed SQL query using source-specific schema and rules.
 */
async function requestExternalToolQueryFix(
	failedSql: string,
	errorMessage: string,
	component: Component,
	executedTools: ExecutedToolInfo[],
	toolId: string,
	apiKey?: string,
	model?: string,
	signal?: AbortSignal,
): Promise<string> {
	// Find the executed tool to get source-specific schema and type
	const executedTool = executedTools.find(t => t.id === toolId);
	const sourceSchema = executedTool?.sourceSchema || 'Schema not available';
	const sourceType = executedTool?.sourceType || 'postgresql';

	// Load database rules for this specific source type
	const databaseRules = await promptLoader.loadDatabaseRulesForType(sourceType);

	const prompt = `You are a SQL expert. Fix the following SQL query that failed execution.

## Database Schema
${sourceSchema}

## Database-Specific SQL Rules
${databaseRules}

## Component Context
- Component Name: ${component.name}
- Component Type: ${component.type}
- Title: ${component.props?.title || 'N/A'}

## Failed Query
\`\`\`sql
${failedSql}
\`\`\`

## Error Message
${errorMessage}

## Instructions
1. Analyze the error message and identify what caused the query to fail
2. Fix the query to resolve the error while preserving the original intent
3. Ensure the fixed query follows the database-specific SQL rules above
4. Return ONLY the fixed SQL query, no explanations or markdown

Fixed SQL query:`;

	const response = await LLM.text(
		{
			sys: 'You are a SQL expert. Return only the fixed SQL query with no additional text, explanations, or markdown formatting.',
			user: prompt,
		},
		{
			model: model || DEFAULT_COMPONENT_MODEL,
			maxTokens: 2048,
			temperature: 0,
			apiKey,
			signal,
		}
	);

	// Clean up response
	let fixedQuery = response.trim();
	fixedQuery = fixedQuery.replace(/^```sql\s*/i, '').replace(/\s*```$/i, '');
	fixedQuery = fixedQuery.replace(/^```\s*/i, '').replace(/\s*```$/i, '');

	const { query: validatedQuery } = validateAndFixSqlQuery(fixedQuery);
	return validatedQuery;
}
